/**
 * PayrollRunDetailPage — Bérszámfejtési futtatás részletei
 * Summary card + stepper + STÁTUSZ-vezérelt workflow akció gombok + export linkek + PayrollItemTable
 *
 * ⚠️ A gombok láthatóságát KIZÁRÓLAG a `run.status` vezérli — ez a lap nem tartalmaz
 * szerep-ellenőrzést, és nincs is szerep-táblája. A jogosultságot a BE `RolesGuard`
 * dönti el (`@Roles`), a 403 pedig a mutációk onError-ágán át válik láthatóvá.
 * (Q-T215 FE-kísérő, 2026-08-03: a korábbi „role-based" docstring olyan affordance-t
 * állított, amit a kód nem hordoz — ugyanaz az alak, mint a guard `:27` hamis
 * „mirrors @kgc/users" kommentje.)
 */

import { useApiError } from '@/hooks/use-api-error';
import { useTranslation } from '@kgc/i18n';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, toast } from '@kgc/ui';
import { useNavigate, useParams } from 'react-router-dom';
import type { PayrollRunStatus } from '../api/payroll-api';
import { exportUrl } from '../api/payroll-api';
import { PayrollItemTable } from '../components/PayrollItemTable';
import { PayrollRunStepper } from '../components/PayrollRunStepper';
import { useApprovePayroll } from '../hooks/useApprovePayroll';
import { useCalculatePayroll } from '../hooks/useCalculatePayroll';
import { usePayrollRunDetail } from '../hooks/usePayrollRunDetail';

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const STATUS_BADGE: Record<PayrollRunStatus, { variant: BadgeVariant; labelKey: string }> = {
  DRAFT: { variant: 'secondary', labelKey: 'payroll.status.DRAFT' },
  CALCULATED: { variant: 'outline', labelKey: 'payroll.status.CALCULATED' },
  BOLT_APPROVED: { variant: 'outline', labelKey: 'payroll.status.BOLT_APPROVED' },
  CENTRAL_REVIEWED: { variant: 'default', labelKey: 'payroll.status.CENTRAL_REVIEWED' },
  FINALIZED: { variant: 'default', labelKey: 'payroll.status.FINALIZED' },
};

export function PayrollRunDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getErrorMessage } = useApiError();

  const { data: run, isLoading, error } = usePayrollRunDetail(id);
  const { mutateAsync: calculate, isPending: isCalculating } = useCalculatePayroll();
  const { mutateAsync: approve, isPending: isApproving } = useApprovePayroll();

  /**
   * A workflow-mutációk hibája NEM nyelhető el: a BE `RolesGuard` 403-a
   * (`INSUFFICIENT_ROLE`) és minden más hiba lokalizált toastot kap.
   * A modul-család kanonikus mintája: LeaveCalendarView.tsx:160-163.
   *
   * ⚠️ `mutateAsync` + try/catch, NEM `mutate(vars, { onError })`. A régi alak
   * KÉT FÜGGETLEN ponton nyelt el (mérve 2026-08-03, `@tanstack/query-core` 5.90.19 +
   * `@tanstack/react-query`; a második a 🔨 pont-verify lelete):
   *   1. `useMutation.js:33` — a `mutate` wrapper `.catch(noop)`-pal nyeli a rejectet
   *   2. `mutationObserver.js:98` — a hívás-szintű callback `hasListeners()` mögött van,
   *      ezért unmountkor (a felhasználó elnavigál a válasz előtt) nem fut le
   * A `mutateAsync` MINDKETTŐT kikerüli: `useMutation.js:40` szerint az a nyers
   * `observer.mutate`, az pedig (`mutationObserver.js:74`) gate nélkül adja vissza az
   * `execute()` promise-t (`mutation.js:175`) — így a catch a komponens closure-jében
   * listener-től függetlenül lefut.
   *
   * ⚠️ A helper KÉSZ szöveget vesz át, NEM kulcsot: a fordítás-hívásnak a HÍVÁS HELYÉN,
   * statikus kulcs-literállal kell állnia, különben az `i18n-key-guard.spec` szkennere
   * (`:37-39`) nem látja, és a ×4 locale-fedettség némán kiesne az ellenőrzés alól.
   * Mérve 2026-08-03: dinamikus kulcs mellett a guard a hiányzó uk-fordítást NEM jelezte.
   *
   * ⚠️ A szkenner a KOMMENTEKET is olvassa — ezért nem szerepel itt példa-hívás
   * literállal; az „álkulcs" ÚJ hiányzó kulcsként bukna el a merge-blokkoló guardon.
   */
  const runWithErrorToast = async (action: () => Promise<unknown>, fallback: string) => {
    try {
      await action();
    } catch (err) {
      toast.error(getErrorMessage(err, fallback));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="p-6">
        <p className="text-destructive">{t('common.error')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/payroll/runs')}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  const badge = STATUS_BADGE[run.status];
  const monthKey = MONTH_KEYS[run.month - 1];
  const monthLabel = monthKey ? t(`common.months.${monthKey}`) : String(run.month);
  const isPending = isCalculating || isApproving;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/payroll/runs')}>
            {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold">
            {run.year} {monthLabel}
            {run.isKivaRun && (
              <span className="ml-2 text-base font-normal text-muted-foreground">(KIVA)</span>
            )}
          </h1>
          <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>
        </div>
      </div>

      {/* Stepper */}
      <Card>
        <CardContent className="pt-6 pb-4">
          <PayrollRunStepper currentStatus={run.status} />
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('common.summary')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('payroll.runs.employeeCount')}</p>
              <p className="text-xl font-semibold">{run.employeeCount}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('payroll.runs.totalGross')}</p>
              <p className="text-xl font-semibold">{run.totalGross.toLocaleString('hu-HU')} Ft</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('payroll.runs.totalNet')}</p>
              <p className="text-xl font-semibold">{run.totalNet.toLocaleString('hu-HU')} Ft</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('payroll.runs.totalEmployerCost')}</p>
              <p className="text-xl font-semibold">
                {run.totalEmployerCost.toLocaleString('hu-HU')} Ft
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('common.actions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* DRAFT → CALCULATED */}
            {run.status === 'DRAFT' && (
              <Button
                disabled={isPending}
                onClick={() => {
                  void runWithErrorToast(
                    () => calculate(run.id),
                    t('payroll.actions.calculateError')
                  );
                }}
              >
                {isCalculating ? t('common.processing') : t('payroll.actions.calculate')}
              </Button>
            )}

            {/* CALCULATED → BOLT_APPROVED */}
            {run.status === 'CALCULATED' && (
              <Button
                disabled={isPending}
                onClick={() => {
                  void runWithErrorToast(
                    () => approve({ id: run.id, stage: 'bolt' }),
                    t('payroll.actions.approveError')
                  );
                }}
              >
                {isApproving ? t('common.processing') : t('payroll.actions.boltApprove')}
              </Button>
            )}

            {/* BOLT_APPROVED → CENTRAL_REVIEWED */}
            {run.status === 'BOLT_APPROVED' && (
              <Button
                disabled={isPending}
                onClick={() => {
                  void runWithErrorToast(
                    () => approve({ id: run.id, stage: 'central' }),
                    t('payroll.actions.approveError')
                  );
                }}
              >
                {isApproving ? t('common.processing') : t('payroll.actions.centralReview')}
              </Button>
            )}

            {/* CENTRAL_REVIEWED → FINALIZED */}
            {run.status === 'CENTRAL_REVIEWED' && (
              <Button
                disabled={isPending}
                onClick={() => {
                  void runWithErrorToast(
                    () => approve({ id: run.id, stage: 'finalize' }),
                    t('payroll.actions.approveError')
                  );
                }}
              >
                {isApproving ? t('common.processing') : t('payroll.actions.finalize')}
              </Button>
            )}

            {/* Export buttons — csak FINALIZED-nél */}
            {run.status === 'FINALIZED' && (
              <>
                <Button variant="outline" asChild>
                  <a href={exportUrl(run.id, 'csv')} download>
                    {t('payroll.actions.exportCsv')}
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href={exportUrl(run.id, 'xlsx')} download>
                    {t('payroll.actions.exportExcel')}
                  </a>
                </Button>
              </>
            )}
          </div>

          {/* Approval timestamps */}
          <div className="mt-4 space-y-1 text-sm text-muted-foreground">
            {run.boltApprovedAt && (
              <p>
                Bolt jóváhagyva: {new Date(run.boltApprovedAt).toLocaleString('hu-HU')}
                {run.boltApprovedBy && ` (${run.boltApprovedBy})`}
              </p>
            )}
            {run.centralReviewedAt && (
              <p>
                Központi review: {new Date(run.centralReviewedAt).toLocaleString('hu-HU')}
                {run.centralReviewedBy && ` (${run.centralReviewedBy})`}
              </p>
            )}
            {run.finalizedAt && (
              <p>
                Véglegesítve: {new Date(run.finalizedAt).toLocaleString('hu-HU')}
                {run.finalizedBy && ` (${run.finalizedBy})`}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* PayrollItemTable */}
      <div>
        <h2 className="text-lg font-semibold mb-3">{t('payroll.items.tableTitle')}</h2>
        <PayrollItemTable runId={run.id} />
      </div>
    </div>
  );
}
